import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils/cn';
import { formatCurrency } from '@/lib/utils/format';
import { ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface ComparisonValue {
  label: string;
  original: number;
  simulated: number;
  format?: 'currency' | 'percent' | 'number';
  currency?: string;
}

interface ComparisonCardProps {
  title: string;
  values: ComparisonValue[];
  badge?: { label: string; variant: 'success' | 'warning' | 'danger' };
}

export function ComparisonCard({ title, values, badge }: ComparisonCardProps) {
  const formatValue = (val: number, format?: string, currency?: string) => {
    if (format === 'currency') return formatCurrency(val, currency || 'USD');
    if (format === 'percent') return `${val.toFixed(1)}%`;
    return val.toLocaleString('es-MX', { maximumFractionDigits: 2 });
  };

  const badgeColors = {
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-sm">{title}</h4>
          {badge && (
            <span
              className={cn(
                'px-2.5 py-0.5 rounded-full text-xs font-semibold',
                badgeColors[badge.variant]
              )}
            >
              {badge.label}
            </span>
          )}
        </div>

        <div className="space-y-3">
          {values.map((v) => {
            const diff = v.simulated - v.original;
            const isPositive = diff > 0;
            const isNeutral = diff === 0;
            const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;

            return (
              <div key={v.label} className="grid grid-cols-[1fr,auto,1fr,auto] items-center gap-2">
                <div>
                  <p className="text-xs text-muted-foreground">{v.label}</p>
                  <p className="text-sm font-medium">
                    {formatValue(v.original, v.format, v.currency)}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Simulado</p>
                  <p className="text-sm font-medium">
                    {formatValue(v.simulated, v.format, v.currency)}
                  </p>
                </div>
                <div
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium',
                    isNeutral
                      ? 'text-muted-foreground'
                      : isPositive
                        ? 'text-emerald-600'
                        : 'text-red-600'
                  )}
                >
                  <TrendIcon className="w-3 h-3" />
                  <span>
                    {isNeutral
                      ? '0'
                      : `${isPositive ? '+' : ''}${formatValue(diff, v.format, v.currency)}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
